import { PureComponent } from 'preact-compat'
import Button from '@insided/control/uikit/Button'
import { Poll } from '../interfaces'
import { ContentType } from '@insided/shared/utils/contentType'
import { editPollOptions, editPollTitle } from '../services/topicService'

/* eslint-disable import/no-internal-modules */
import InputGroup from '@insided/deprecated-control/ui-components/InputGroup'
import InputGroupWithIcons from '@insided/deprecated-control/ui-components/InputGroupWithIcons'
import Icon from '@insided/deprecated-preact-components/assets'
import DeletePollModal from '@insided/control/modules/ContentModeration/TopicModeration/post/DeletePollModal'

export interface PollEditorProps {
  poll: Poll
  onShowAlert: (type: 'success' | 'error', message?: string) => void
  contentType: ContentType
  topicId: number
  onPollUpdate: (data: Pick<Poll, 'question'> | Pick<Poll, 'options'>) => void
  onTogglePollEditor: (show: boolean) => void
}

export interface PollEditorState {
  newOption: string
  isLoading: boolean
  showDeleteModal: boolean
}

class PollEditor extends PureComponent<PollEditorProps, PollEditorState> {
  constructor(props: PollEditorProps) {
    super(props)
    this.onUpdateOption = this.onUpdateOption.bind(this)
    this.onSavePoll = this.onSavePoll.bind(this)
    this.onCloseModal = this.onCloseModal.bind(this)

    this.state = {
      isLoading: false,
      newOption: '',
      showDeleteModal: false
    }
  }

  async onUpdateOption(newValue: string, idx: number) {
    const { poll, onPollUpdate } = this.props
    if (poll.options) {
      poll.options[idx] = newValue
      onPollUpdate({ options: this.props.poll.options })
    }
  }

  async onDeleteOption(index: number) {
    const { poll, onPollUpdate } = this.props
    const newList = poll.options
    newList?.splice(index, 1)
    onPollUpdate({ options: newList })
  }

  async onSavePoll() {
    const { contentType, topicId, onShowAlert, poll, onTogglePollEditor } = this.props
    this.setState({ isLoading: true })
    try {
      await editPollTitle(contentType, topicId, poll.question)
      poll.options && (await editPollOptions(contentType, topicId, poll.options))
      onTogglePollEditor(false)
      this.setState({ isLoading: false })
      onShowAlert('success', 'Poll successfully updated')
    } catch (e) {
      onShowAlert('error', `${e?.message}`)
    }
    this.setState({ isLoading: false })
  }

  onCloseModal() {
    this.setState({ showDeleteModal: false })
  }

  render() {
    const { onPollUpdate, poll, onTogglePollEditor, contentType, topicId, onShowAlert } = this.props
    const { showDeleteModal } = this.state
    const hasVote = poll.voteCount?.some((vote) => vote > 0)
    const hasLessThanTwoOptions = poll.options && poll.options.length <= 2

    return (
      <div className='poll--content'>
        <div>
          <InputGroup
            id='edit-poll-question'
            title='Poll question:'
            type='text'
            onChange={(e) => onPollUpdate({ question: e.target.value })}
            value={poll.question}
            placeholder='Add your question (minimum 10 characters)'
            disabled={hasVote}
          />
          <p className='poll--title'>Poll options:</p>
          {poll.options?.map((option, index) =>
            hasVote || hasLessThanTwoOptions ? (
              <InputGroup
                id='edit-poll-option'
                title=''
                type='text'
                onChange={async (e) => await this.onUpdateOption(e.target.value, index)}
                value={option}
                placeholder='+ Insert an answer here'
                disabled={hasVote}
              />
            ) : (
              <InputGroupWithIcons
                attributes={{
                  type: 'text',
                  value: option,
                  placeholder: '+ Insert an answer here'
                }}
                className='qa-poll-option'
                postIcon={
                  <Icon onClick={async () => await this.onDeleteOption(index)} icon='close' size={16} color='#2B3346' />
                }
                onChange={async (newValue: string) => await this.onUpdateOption(newValue, index)}
                disabled={hasVote}
              />
            )
          )}
          <Button
            title='Save changes'
            variant='save'
            size='small'
            disabled={hasVote}
            onClick={this.onSavePoll}
            data-qa='save-poll'
          />
          <Button
            title='Delete poll'
            variant='danger'
            size='small'
            onClick={() => this.setState({ showDeleteModal: true })}
            data-qa='delete-poll'
          />
          <Button title='Cancel' variant='text' onClick={() => onTogglePollEditor(false)} />
        </div>
        {showDeleteModal && (
          <DeletePollModal
            contentType={contentType}
            topicId={topicId}
            onPollUpdate={onPollUpdate}
            onTogglePollEditor={onTogglePollEditor}
            onShowAlert={onShowAlert}
            onClose={this.onCloseModal}
          />
        )}
      </div>
    )
  }
}

export default PollEditor
